/**
 * TTS 资源库管理（旧 views/tts/TTSResourceView.vue）：搜索筛选、分页、试听、详情、单个和批量删除
 */
import { useEffect, useState } from 'react'
import {
  Alert,
  App,
  Badge,
  Button,
  Card,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  type TableColumnsType
} from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { PageHeader } from '@/components/PageHeader'
import type { TTSAudio } from '@/api/services/tts'
import {
  PROVIDER_OPTIONS,
  STATUS_OPTIONS,
  audioUrl,
  formatCost,
  formatFileSize,
  providerColor,
  statusColor,
  statusText,
  truncateText
} from './constants'
import { useBatchDeleteTTS, useDeleteTTS, useTTSList } from './hooks'
import { TTSDetailDrawer } from './TTSDetailDrawer'
import styles from './TTSResourcePage.module.css'

interface TTSFilters {
  search?: string
  provider?: string
  generation_status?: string
}

export function TTSResourcePage() {
  const { message } = App.useApp()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  // draft 是表单里正在编辑的条件，filters 是已经提交查询的条件
  const [draft, setDraft] = useState<TTSFilters>({})
  const [filters, setFilters] = useState<TTSFilters>({})
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [detail, setDetail] = useState<TTSAudio | null>(null)
  const { data, isFetching, isError } = useTTSList({ page, page_size: pageSize, ...filters })
  const deleteAudio = useDeleteTTS()
  const batchDelete = useBatchDeleteTTS()

  useEffect(() => {
    if (isError) message.error('获取TTS资源列表失败')
  }, [isError, message])

  // 查询时回到第一页
  const applyFilters = (next: TTSFilters) => {
    setFilters({ ...next, search: next.search || undefined })
    setPage(1)
  }

  // 下拉框改变时立即查询（与旧版一致）
  const changeSelect = (key: 'provider' | 'generation_status', value: string | undefined) => {
    const next = { ...draft, [key]: value }
    setDraft(next)
    applyFilters(next)
  }

  const resetFilters = () => {
    setDraft({})
    applyFilters({})
  }

  const handleDelete = async (id: number) => {
    try {
      await deleteAudio.mutateAsync(id)
      message.success('删除成功')
    } catch (error) {
      console.error('删除失败:', error)
      message.error('删除失败')
    }
  }

  const handleBatchDelete = async () => {
    try {
      await batchDelete.mutateAsync(selectedIds)
      message.success(`成功删除 ${selectedIds.length} 条音频资源`)
      setSelectedIds([])
    } catch (error) {
      console.error('批量删除失败:', error)
      message.error('批量删除失败')
    }
  }

  const muted = (text: string) => <span className={styles.muted}>{text}</span>

  const columns: TableColumnsType<TTSAudio> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80, fixed: 'left' },
    {
      title: '文本内容',
      key: 'text_content',
      width: 250,
      ellipsis: true,
      render: (_, record) =>
        record.text_content ? (
          <Tooltip title={record.text_content}>
            <span>{truncateText(record.text_content, 50)}</span>
          </Tooltip>
        ) : (
          muted('长文本（未缓存）')
        )
    },
    { title: '文本长度', dataIndex: 'text_length', key: 'text_length', width: 100 },
    {
      title: '音频播放',
      key: 'audio_player',
      width: 220,
      // 音频由同一行的文本合成，文本列就是它的文字版，不另配字幕轨道
      render: (_, record) => (
        <audio // NOSONAR
          className={styles.rowAudio}
          src={audioUrl(record.audio_url)}
          controls
          preload="none"
        />
      )
    },
    {
      title: '供应商',
      key: 'provider',
      width: 120,
      render: (_, record) => <Tag color={providerColor(record.provider)}>{record.provider}</Tag>
    },
    {
      title: '状态',
      key: 'generation_status',
      width: 100,
      render: (_, record) => (
        <Tag color={statusColor(record.generation_status)}>{statusText(record.generation_status)}</Tag>
      )
    },
    {
      title: '文件大小',
      key: 'audio_file_size_bytes',
      width: 100,
      render: (_, record) =>
        record.audio_file_size_bytes ? formatFileSize(record.audio_file_size_bytes) : muted('-')
    },
    {
      title: '使用次数',
      key: 'usage_count',
      width: 100,
      render: (_, record) => <Badge count={record.usage_count} color="green" />
    },
    {
      title: '成本',
      key: 'cost_usd',
      width: 100,
      render: (_, record) => (record.cost_usd ? formatCost(record.cost_usd, 4) : muted('-'))
    },
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 180 },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button size="small" type="link" onClick={() => setDetail(record)}>
            详情
          </Button>
          <Popconfirm
            title="确定删除这条音频资源吗？音频文件也会被删除。"
            okText="删除"
            cancelText="取消"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button danger size="small" type="link">
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div>
      <PageHeader title="TTS资源库管理" subtitle="管理和配置文本转语音资源" />

      <Card variant="borderless">
        <Form layout="inline" className={styles.filters}>
          <Form.Item label="搜索">
            <Input
              aria-label="搜索"
              placeholder="搜索文本内容、音频URL、供应商等"
              style={{ width: 300 }}
              prefix={<SearchOutlined />}
              value={draft.search}
              onChange={(event) => setDraft((current) => ({ ...current, search: event.target.value }))}
              onPressEnter={() => applyFilters(draft)}
            />
          </Form.Item>
          <Form.Item label="供应商">
            <Select
              aria-label="供应商"
              placeholder="选择供应商"
              style={{ width: 150 }}
              allowClear
              value={draft.provider}
              options={PROVIDER_OPTIONS}
              onChange={(value) => changeSelect('provider', value)}
            />
          </Form.Item>
          <Form.Item label="状态">
            <Select
              aria-label="状态"
              placeholder="选择状态"
              style={{ width: 150 }}
              allowClear
              value={draft.generation_status}
              options={STATUS_OPTIONS}
              onChange={(value) => changeSelect('generation_status', value)}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" onClick={() => applyFilters(draft)}>
              查询
            </Button>
          </Form.Item>
          <Form.Item>
            <Button onClick={resetFilters}>重置</Button>
          </Form.Item>
        </Form>

        {selectedIds.length > 0 && (
          <Alert
            className={styles.batchBar}
            type="info"
            showIcon
            title={`已选择 ${selectedIds.length} 项`}
            action={
              <Popconfirm
                title={`确定删除选中的 ${selectedIds.length} 条音频资源吗？音频文件也会被删除。`}
                okText="删除"
                cancelText="取消"
                onConfirm={handleBatchDelete}
              >
                <Button danger size="small">
                  批量删除
                </Button>
              </Popconfirm>
            }
          />
        )}

        <Table<TTSAudio>
          rowKey="id"
          columns={columns}
          dataSource={data?.items}
          loading={isFetching}
          scroll={{ x: 1500 }}
          rowSelection={{
            selectedRowKeys: selectedIds,
            onChange: (keys) => setSelectedIds(keys as number[])
          }}
          pagination={{
            current: page,
            pageSize,
            total: data?.total ?? 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`
          }}
          onChange={(pagination) => {
            setPage(pagination.current ?? 1)
            setPageSize(pagination.pageSize ?? 20)
          }}
        />
      </Card>

      <TTSDetailDrawer audio={detail} onClose={() => setDetail(null)} />
    </div>
  )
}
